import { SwipeCard } from './SwipeCard';
import { ProofCaptureSheet, ProofType } from './ProofCaptureSheet';
import { cn } from '@/lib/utils';
import { Clock, AlertTriangle, FileText, ChevronDown, CheckCircle2, Camera, FileUp, MessageSquare } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface TaskCardData {
  id: string;
  title: string;
  instruction?: string | null;
  runName?: string;
  sopTitle?: string;
  dueAt?: string | null;
  isCritical?: boolean;
  proofType?: string;
  status?: string;
}

interface TaskSwipeCardProps {
  task: TaskCardData;
  onComplete: (taskId: string) => void;
  onSkip: (taskId: string) => void;
  onTap?: (taskId: string) => void;
  isTopCard?: boolean;
}

export function TaskSwipeCard({
  task,
  onComplete,
  onSkip,
  onTap,
  isTopCard = true,
}: TaskSwipeCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showProofSheet, setShowProofSheet] = useState(false);
  const [pendingAction, setPendingAction] = useState<'complete' | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const { toast } = useToast();

  // Get current user
  useState(() => {
    supabase.auth.getUser().then(({ data }) => {
      setCurrentUserId(data.user?.id || null);
    });
  });

  const getDueStatus = () => {
    if (!task.dueAt) return null;
    
    const dueDate = new Date(task.dueAt);
    const now = new Date();
    const isOverdue = dueDate < now;
    const timeText = formatDistanceToNow(dueDate, { addSuffix: true });
    
    return {
      isOverdue,
      text: isOverdue ? `Overdue ${timeText}` : `Due ${timeText}`,
    };
  };

  const dueStatus = getDueStatus();
  const proofType = (task.proofType || 'NONE') as ProofType;
  const needsProof = proofType !== 'NONE';

  const getProofIcon = () => {
    switch (proofType) {
      case 'PHOTO_UPLOAD': return <Camera className="h-3 w-3" />;
      case 'FILE_UPLOAD': return <FileUp className="h-3 w-3" />;
      case 'TEXT_NOTE': return <MessageSquare className="h-3 w-3" />;
      default: return <FileText className="h-3 w-3" />;
    }
  };

  const getProofLabel = () => {
    switch (proofType) {
      case 'PHOTO_UPLOAD': return 'Photo';
      case 'FILE_UPLOAD': return 'File';
      case 'TEXT_NOTE': return 'Note';
      default: return 'Proof';
    }
  };

  const handleSwipeRight = useCallback(() => {
    if (needsProof) {
      // Open proof capture sheet
      setPendingAction('complete');
      setShowProofSheet(true);
    } else {
      // Complete directly
      onComplete(task.id);
    }
  }, [needsProof, task.id, onComplete]);

  const handleProofSubmit = async (proof: { type: ProofType; value: string; fileUrl?: string }) => {
    try {
      if (!currentUserId) throw new Error('Not authenticated');

      // Create proof attachment
      await supabase
        .from('proof_attachments')
        .insert({
          sop_run_step_id: task.id,
          proof_type: proof.type,
          text_value: proof.type === 'TEXT_NOTE' ? proof.value : null,
          file_url: proof.fileUrl || null,
          uploaded_by_user_id: currentUserId,
        });

      // Now complete the task
      if (pendingAction === 'complete') {
        onComplete(task.id);
      }
      
      toast({
        title: 'Done. Moving on.',
      });
    } catch (error) {
      console.error('Error saving proof:', error);
      toast({
        variant: 'destructive',
        title: 'Something broke',
        description: 'Trying again might help.',
      });
    }
  };

  const handleTap = () => {
    if (onTap) {
      onTap(task.id);
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <>
      <SwipeCard
        onSwipeLeft={() => onSkip(task.id)}
        onSwipeRight={handleSwipeRight}
        onTap={handleTap}
        isTopCard={isTopCard}
        leftLabel="Skip"
        rightLabel={needsProof ? `+ ${getProofLabel()}` : 'Done'}
        className="w-full h-full"
      >
        <div className="p-6 h-full flex flex-col">
          {/* Header badges */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {task.isCritical && (
              <Badge variant="destructive" className="gap-1">
                <AlertTriangle className="h-3 w-3" />
                Critical
              </Badge>
            )}
            {dueStatus && (
              <Badge 
                variant={dueStatus.isOverdue ? 'destructive' : 'secondary'}
                className="gap-1"
              >
                <Clock className="h-3 w-3" />
                {dueStatus.text}
              </Badge>
            )}
            {needsProof && (
              <Badge variant="outline" className="gap-1 border-primary/50 text-primary">
                {getProofIcon()}
                {getProofLabel()} required
              </Badge>
            )}
          </div>

          {/* Task title */}
          <h3 className="text-xl font-semibold text-foreground mb-2 leading-tight">
            {task.title}
          </h3>

          {/* Context */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            {task.sopTitle && (
              <span className="truncate">{task.sopTitle}</span>
            )}
            {task.sopTitle && task.runName && (
              <span>•</span>
            )}
            {task.runName && (
              <span className="truncate">{task.runName}</span>
            )}
          </div>

          {/* Instruction preview / expanded */}
          {task.instruction && (
            <div className={cn(
              'flex-1 overflow-hidden transition-all duration-300',
              isExpanded ? 'max-h-48' : 'max-h-16'
            )}>
              <p className={cn(
                'text-muted-foreground text-sm leading-relaxed',
                !isExpanded && 'line-clamp-3'
              )}>
                {task.instruction}
              </p>
            </div>
          )}

          {/* Expand hint */}
          {task.instruction && !isExpanded && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(true);
              }}
              className="flex items-center gap-1 text-xs text-muted-foreground mt-2 hover:text-foreground transition-colors"
            >
              <ChevronDown className="h-3 w-3" />
              Tap to see more
            </button>
          )}

          {/* Spacer */}
          <div className="flex-1 min-h-4" />

          {/* Bottom action hint */}
          <div className="flex items-center justify-center gap-6 pt-4 border-t border-border/50">
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm">
                ←
              </div>
              <span className="text-sm">Skip</span>
            </div>
            <div className="w-px h-6 bg-border" />
            <div className="flex items-center gap-2 text-success">
              <span className="text-sm font-medium">
                {needsProof ? `Add ${getProofLabel()}` : 'Complete'}
              </span>
              <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center">
                {needsProof ? getProofIcon() : <CheckCircle2 className="h-4 w-4" />}
              </div>
            </div>
          </div>
        </div>
      </SwipeCard>

      {/* Proof Capture Sheet */}
      {currentUserId && (
        <ProofCaptureSheet
          open={showProofSheet}
          onOpenChange={setShowProofSheet}
          proofType={proofType}
          taskTitle={task.title}
          taskId={task.id}
          userId={currentUserId}
          onSubmit={handleProofSubmit}
          onSkip={() => {
            setShowProofSheet(false);
            onComplete(task.id);
          }}
        />
      )}
    </>
  );
}
